import React from "react";

/**
 * Diagrama del Método de Alineación de Glosarios LEL.
 *
 * Réplica en SVG del esquema del autor: dos glosarios de entrada (LEL1, LEL2)
 * alimentan el método, que preprocesa y ejecuta las cuatro comparaciones
 * (Nombre, Tipo, Noción, Impacto) apoyándose en dos recursos laterales
 * (Alineación Léxico-Semántica y Diccionario de Sinónimos), para producir los
 * tres resultados posibles: Similitudes, Diferencias y Omisiones.
 *
 * Las cuatro comparaciones se disponen en rejilla 2x2 en lugar de apiladas:
 * son simultáneas, no secuenciales, y así el diagrama ocupa mucha menos altura
 * en pantalla (relación ~1:0.75 en vez de ~1:1.3).
 *
 * Se dibuja como SVG inline —sin librerías ni imágenes externas— para que
 * escale nítido a cualquier tamaño y siga la paleta de estados de la app.
 */

// Paleta de estados LEL (celeste -> azul), la misma del resto de la app.
const COLOR_SIMILITUD = "#00008B";
const COLOR_DIFERENCIA = "#1E90FF";
const COLOR_OMISION = "#87CEFA";

const AZUL_BORDE = "#1E3A8A";
const GRIS_BORDE = "#64748B";
const FONDO_CAJA = "#FFFFFF";
const FONDO_PANEL = "#F1F5F9";
const FONDO_INTERNO = "#E8EEF7";

const DiagramaMetodologia = () => (
  <svg
    viewBox="0 0 360 272"
    role="img"
    aria-label="Diagrama del método de alineación de glosarios LEL: los glosarios LEL1 y LEL2 entran al método de alineación, que aplica preprocesamiento y compara nombre, tipo, noción e impacto, apoyado en la alineación léxico-semántica y un diccionario de sinónimos, produciendo similitudes, diferencias y omisiones."
    style={{ width: "100%", height: "auto", display: "block" }}
  >
    <defs>
      <marker
        id="flechaMet"
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="5"
        markerHeight="5"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill={AZUL_BORDE} />
      </marker>
    </defs>

    {/* ---- Entradas: LEL1 y LEL2 (forma de documento) ---- */}
    {[
      { x: 118, label: "LEL1" },
      { x: 196, label: "LEL2" },
    ].map(({ x, label }) => (
      <g key={label}>
        <path
          d={`M ${x} 5 h 46 v 22 q -11 -6 -23 0 q -12 6 -23 0 z`}
          fill={FONDO_CAJA}
          stroke={AZUL_BORDE}
          strokeWidth="1.3"
        />
        <text
          x={x + 23}
          y="20"
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          fill={AZUL_BORDE}
        >
          {label}
        </text>
        <line
          x1={x + 23}
          y1="29"
          x2={x + 23}
          y2="42"
          stroke={AZUL_BORDE}
          strokeWidth="1.3"
          markerEnd="url(#flechaMet)"
        />
      </g>
    ))}

    {/* ---- Panel principal: Método de Alineación ---- */}
    <rect
      x="74"
      y="44"
      width="212"
      height="164"
      rx="4"
      fill={FONDO_PANEL}
      stroke={AZUL_BORDE}
      strokeWidth="1.3"
    />
    <text
      x="180"
      y="59"
      textAnchor="middle"
      fontSize="11"
      fontWeight="700"
      fill={AZUL_BORDE}
    >
      Método de Alineación
    </text>

    {/* Preprocesamiento */}
    <rect
      x="92"
      y="66"
      width="176"
      height="21"
      rx="3"
      fill={FONDO_CAJA}
      stroke={AZUL_BORDE}
      strokeWidth="1.1"
    />
    <text x="180" y="80" textAnchor="middle" fontSize="10" fill="#1E293B">
      Preprocesamiento
    </text>
    <line
      x1="180"
      y1="87"
      x2="180"
      y2="97"
      stroke={AZUL_BORDE}
      strokeWidth="1.3"
      markerEnd="url(#flechaMet)"
    />

    {/* Bloque de comparaciones (rejilla 2x2) */}
    <rect
      x="90"
      y="99"
      width="180"
      height="72"
      rx="4"
      fill={FONDO_INTERNO}
      stroke={AZUL_BORDE}
      strokeWidth="1.1"
    />
    {[
      { x: 96, y: 105, label: "Compara Nombre" },
      { x: 184, y: 105, label: "Compara Tipo" },
      { x: 96, y: 139, label: "Compara Noción" },
      { x: 184, y: 139, label: "Compara Impacto" },
    ].map(({ x, y, label }) => (
      <g key={label}>
        <rect
          x={x}
          y={y}
          width="80"
          height="26"
          rx="3"
          fill={FONDO_CAJA}
          stroke={AZUL_BORDE}
          strokeWidth="1.1"
        />
        <text
          x={x + 40}
          y={y + 17}
          textAnchor="middle"
          fontSize="8.5"
          fill="#1E293B"
        >
          {label}
        </text>
      </g>
    ))}

    {/* Del bloque de comparaciones a Resultados */}
    <line
      x1="180"
      y1="171"
      x2="180"
      y2="180"
      stroke={AZUL_BORDE}
      strokeWidth="1.3"
      markerEnd="url(#flechaMet)"
    />
    <text
      x="180"
      y="196"
      textAnchor="middle"
      fontSize="10.5"
      fontWeight="700"
      fill={AZUL_BORDE}
    >
      Resultados
    </text>
    <line
      x1="180"
      y1="201"
      x2="180"
      y2="218"
      stroke={AZUL_BORDE}
      strokeWidth="1.3"
    />

    {/* ---- Recursos laterales (texto vertical) ---- */}
    {[
      { x: 6, lineas: ["Alineación", "Léxico-Semántico"], izq: true },
      { x: 322, lineas: ["Diccionario", "de Sinónimos"], izq: false },
    ].map(({ x, lineas, izq }) => {
      const cx = x + 16;
      return (
        <g key={lineas.join(" ")}>
          <rect
            x={x}
            y="99"
            width="32"
            height="72"
            rx="3"
            fill={FONDO_CAJA}
            stroke={GRIS_BORDE}
            strokeWidth="1.1"
          />
          {/* Texto vertical en dos líneas, rotado sobre su propio centro */}
          <text
            textAnchor="middle"
            fontSize="7.5"
            fill="#334155"
            transform={`rotate(-90 ${cx} 135)`}
          >
            {lineas.map((linea, i) => (
              <tspan
                key={linea}
                x={cx}
                y={135 + (i - (lineas.length - 1) / 2) * 9}
              >
                {linea}
              </tspan>
            ))}
          </text>
          <line
            x1={izq ? x + 32 : x}
            y1="135"
            x2={izq ? 88 : 272}
            y2="135"
            stroke={GRIS_BORDE}
            strokeWidth="1.3"
            markerEnd="url(#flechaMet)"
          />
        </g>
      );
    })}

    {/* ---- Salidas: los tres estados, con la paleta de la app ---- */}
    {/* Conectores primero, para que ninguna línea cruce sobre el texto */}
    <line
      x1="62"
      y1="218"
      x2="298"
      y2="218"
      stroke={AZUL_BORDE}
      strokeWidth="1.3"
    />
    {[62, 180, 298].map((x) => (
      <line
        key={x}
        x1={x}
        y1="218"
        x2={x}
        y2="230"
        stroke={AZUL_BORDE}
        strokeWidth="1.3"
        markerEnd="url(#flechaMet)"
      />
    ))}

    {[
      { cx: 62, label: "Similitudes", color: COLOR_SIMILITUD },
      { cx: 180, label: "Diferencias", color: COLOR_DIFERENCIA },
      { cx: 298, label: "Omisiones", color: COLOR_OMISION },
    ].map(({ cx, label, color }) => (
      <g key={label}>
        <rect
          x={cx - 52}
          y="232"
          width="104"
          height="26"
          rx="3"
          fill={FONDO_CAJA}
          stroke={color}
          strokeWidth="1.8"
        />
        <text
          x={cx}
          y="249"
          textAnchor="middle"
          fontSize="10"
          fontWeight="600"
          fill={color === COLOR_OMISION ? "#2B7FA8" : color}
        >
          {label}
        </text>
      </g>
    ))}
  </svg>
);

export default DiagramaMetodologia;
